/**
 * BoardCapturePanel — drawer that lives next to the recorded-audio session.
 *
 * Teacher can:
 *   • Add board photos (web: browser file/camera input; mobile uses native picker
 *     but POSTs to the SAME endpoint — common backend).
 *   • See per-photo OCR status (pending → processing → done | failed).
 *   • Retake if the privacy guard flagged a face on the photo.
 *   • Drag-reorder photos by capture_order (PATCH).
 *   • Run "Generate lesson with board" → fusion → review screen.
 *
 * Backend contract (single source of truth, web + app):
 *   POST   /api/teacher/audio-inbox/{session_id}/board-captures
 *   GET    /api/teacher/audio-inbox/{session_id}/board-captures
 *   PATCH  /api/teacher/board-captures/{capture_id}
 *   DELETE /api/teacher/board-captures/{capture_id}
 *   POST   /api/teacher/audio-inbox/{session_id}/generate-lesson-with-board
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import {
  X, Camera, ImagePlus, Sparkles, RefreshCw, Trash2, CheckCircle2,
  AlertTriangle, Loader2, ChevronLeft, ChevronRight, FileText, Brain, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { confirmDialog } from '@/components/ConfirmDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GREEN = '#34A853';
const SOFT = '#F0FDF4';
const DARK = '#0C1A12';
const POLL_MS = 2500;

function authHeaders() {
  const t = localStorage.getItem('desky_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function fmtOffset(sec) {
  if (sec === null || sec === undefined) return 'no offset';
  if (sec < 60) return `${Math.round(sec)}s`;
  return `${Math.floor(sec / 60)}m ${Math.round(sec % 60)}s`;
}

const STATUS_STYLE = {
  pending:    { bg: '#FEF3C7', fg: '#92400E', label: 'Queued' },
  processing: { bg: '#DBEAFE', fg: '#1E40AF', label: 'OCR…' },
  done:       { bg: '#D1FAE5', fg: '#065F46', label: 'Ready' },
  failed:     { bg: '#FEE2E2', fg: '#991B1B', label: 'Failed' },
};

export default function BoardCapturePanel({ sessionId, sessionTitle, onClose, onDraftReady }) {
  const [captures, setCaptures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const pendingPoll = captures.some(c => ['pending', 'processing'].includes(c.ocr_status));

  const fetchCaps = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/teacher/audio-inbox/${sessionId}/board-captures`, { headers: authHeaders() });
      setCaptures(res.data.items || []);
    } catch (e) {
      // Silent — endpoint may not be ready yet; the button is only enabled on ready sessions
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    let t;
    fetchCaps();
    if (pendingPoll) t = setInterval(fetchCaps, POLL_MS);
    return () => t && clearInterval(t);
  }, [sessionId, pendingPoll, fetchCaps]);

  const uploadOne = async (file, sourcePlatform = 'web') => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('capture_timestamp', new Date().toISOString());
    fd.append('source_platform', sourcePlatform);
    fd.append('idempotency_key', `${sessionId}-${file.name || 'cap'}-${file.size}-${Date.now()}`);
    setUploading(true);
    try {
      await axios.post(`${API}/teacher/audio-inbox/${sessionId}/board-captures`, fd, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Board photo uploaded — OCR running');
      await fetchCaps();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) await uploadOne(f, 'web');
    e.target.value = '';
  };

  const handleCamera = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) await uploadOne(f, 'web');
    e.target.value = '';
  };

  const retake = async (id) => {
    if (!(await confirmDialog({ title: 'Retake photo', message: 'Delete this photo and capture a new one?', confirmLabel: 'Delete', danger: true }))) return;
    try {
      await axios.delete(`${API}/teacher/board-captures/${id}`, { headers: authHeaders() });
      toast.success('Photo deleted — capture a new one');
      fetchCaps();
    } catch (e) {
      toast.error('Could not delete');
    }
  };

  const reorder = async (id, dir) => {
    const idx = captures.findIndex(c => c.capture_id === id);
    const targetIdx = dir === 'left' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= captures.length) return;
    const next = [...captures];
    [next[idx], next[targetIdx]] = [next[targetIdx], next[idx]];
    setCaptures(next.map((c, i) => ({ ...c, capture_order: i })));
    try {
      await axios.patch(`${API}/teacher/board-captures/${id}`,
        { capture_order: targetIdx }, { headers: authHeaders() });
      await axios.patch(`${API}/teacher/board-captures/${captures[targetIdx].capture_id}`,
        { capture_order: idx }, { headers: authHeaders() });
    } catch {
      fetchCaps();
    }
  };

  const generate = async () => {
    const doneCount = captures.filter(c => c.ocr_status === 'done').length;
    if (doneCount === 0 && captures.length > 0) {
      if (!(await confirmDialog('No board photos are ready yet. Generate using audio only?'))) return;
    }
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/teacher/audio-inbox/${sessionId}/generate-lesson-with-board`,
        { use_board_captures: true },
        { headers: authHeaders() },
      );
      toast.success(`Lesson generated with ${res.data.board_captures_used} board photos`);
      if (onDraftReady) onDraftReady(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fusion failed');
    } finally {
      setGenerating(false);
    }
  };

  const active = captures[activeIndex];

  return (
    <div className="fixed inset-0 z-[60] flex" style={{ background: 'rgba(8,16,12,0.72)' }} data-testid="board-capture-panel">
      <div className="ml-auto h-full w-full sm:w-[640px] lg:w-[760px] bg-white shadow-2xl flex flex-col" style={{ maxWidth: '100vw' }}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Board photos · vision OCR + fusion</div>
            <h2 className="text-lg font-bold text-slate-900 mt-0.5" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {sessionTitle || 'Lecture session'}
            </h2>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Add photos of the blackboard. Our AI extracts formulas, diagrams and notes, then fuses them with your recorded audio into one lesson draft.
            </p>
          </div>
          <button onClick={onClose} data-testid="bcp-close" className="text-slate-400 hover:text-slate-700 p-1 -m-1 rounded-md hover:bg-slate-100">
            <X className="w-5 h-5"/>
          </button>
        </div>

        {/* Capture buttons (shared web/mobile contract — endpoint identical) */}
        <div className="px-5 py-3 border-b border-slate-100 flex gap-2 flex-wrap">
          <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/webp"
                 multiple style={{ display: 'none' }} onChange={handleFiles} data-testid="bcp-file-input"/>
          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment"
                 style={{ display: 'none' }} onChange={handleCamera} data-testid="bcp-camera-input"/>
          <button onClick={() => cameraInputRef.current?.click()} disabled={uploading}
                  data-testid="bcp-take-photo"
                  className="h-10 px-4 rounded-lg font-semibold text-white text-sm shadow-sm hover:shadow-md transition-shadow inline-flex items-center gap-2"
                  style={{ background: GREEN }}>
            {uploading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Camera className="w-4 h-4"/>}
            Take board photo
          </button>
          <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                  data-testid="bcp-upload-files"
                  className="h-10 px-4 rounded-lg font-semibold text-emerald-700 text-sm border border-emerald-200 bg-white hover:bg-emerald-50 inline-flex items-center gap-2">
            <ImagePlus className="w-4 h-4"/> Upload images
          </button>
          <div className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1.5">
            <ShieldCheck className="w-3 h-3"/> Faces auto-removed (DPDP §10)
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && (
            <div className="text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/> Loading…</div>
          )}
          {!loading && captures.length === 0 && (
            <div className="text-center py-12 border border-dashed border-slate-200 rounded-2xl" data-testid="bcp-empty">
              <Camera className="w-9 h-9 text-slate-300 mx-auto mb-2"/>
              <p className="text-sm font-semibold text-slate-700">No board photos yet</p>
              <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                Add the first one — its capture time will be aligned to your recorded-audio timeline so the AI can place its content at the right moment in the lesson.
              </p>
            </div>
          )}

          {!loading && captures.length > 0 && (
            <>
              {/* Thumbnail strip */}
              <div className="flex gap-2 overflow-x-auto pb-3 -mx-1 px-1" data-testid="bcp-strip">
                {captures.map((c, i) => {
                  const st = STATUS_STYLE[c.ocr_status] || STATUS_STYLE.pending;
                  const isActive = i === activeIndex;
                  const img = c.processed_image_url || c.raw_image_url;
                  return (
                    <button
                      key={c.capture_id}
                      onClick={() => setActiveIndex(i)}
                      data-testid={`bcp-thumb-${i}`}
                      className={`relative flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden border-2 transition-all ${isActive ? 'border-emerald-500 ring-2 ring-emerald-200' : 'border-slate-200 hover:border-slate-400'}`}>
                      {img ? (
                        <img alt="" src={`${process.env.REACT_APP_BACKEND_URL}${img}`}
                             className="w-full h-full object-cover" loading="lazy"
                             onError={(e) => { e.target.style.display = 'none'; }}/>
                      ) : (
                        <div className="w-full h-full bg-slate-100 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-slate-300"/>
                        </div>
                      )}
                      <span className="absolute bottom-1 left-1 right-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded text-center text-white"
                            style={{ background: st.fg + 'EE' }}>
                        #{i + 1} · {st.label}
                      </span>
                      {c.face_flag && (
                        <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-red-500 flex items-center justify-center" title="Face flagged">
                          <AlertTriangle className="w-2.5 h-2.5 text-white"/>
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Active capture detail */}
              {active && (
                <div className="mt-4 rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="bcp-active">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 gap-2">
                    <div className="flex items-center gap-2">
                      <button onClick={() => reorder(active.capture_id, 'left')} disabled={activeIndex === 0}
                              data-testid="bcp-move-left"
                              className="w-7 h-7 rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-30 flex items-center justify-center">
                        <ChevronLeft className="w-4 h-4"/>
                      </button>
                      <span className="text-xs font-bold text-slate-700">Photo #{activeIndex + 1} of {captures.length}</span>
                      <button onClick={() => reorder(active.capture_id, 'right')} disabled={activeIndex === captures.length - 1}
                              data-testid="bcp-move-right"
                              className="w-7 h-7 rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-30 flex items-center justify-center">
                        <ChevronRight className="w-4 h-4"/>
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-slate-500">
                        offset · <strong className="font-mono text-slate-700">{fmtOffset(active.recording_offset_seconds)}</strong>
                      </span>
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-bold"
                            style={{ background: (STATUS_STYLE[active.ocr_status] || STATUS_STYLE.pending).bg,
                                     color:      (STATUS_STYLE[active.ocr_status] || STATUS_STYLE.pending).fg }}>
                        {(STATUS_STYLE[active.ocr_status] || STATUS_STYLE.pending).label}
                      </span>
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-bold bg-slate-100 text-slate-600">
                        via {active.source_platform}
                      </span>
                      <button onClick={() => retake(active.capture_id)} data-testid="bcp-retake"
                              className="text-slate-400 hover:text-red-600 p-1.5 rounded-md hover:bg-red-50">
                        <Trash2 className="w-3.5 h-3.5"/>
                      </button>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 p-4">
                    {/* Image */}
                    <div className="bg-slate-50 rounded-xl overflow-hidden border border-slate-100 aspect-[4/3] flex items-center justify-center">
                      {(active.processed_image_url || active.raw_image_url) ? (
                        <img alt="" src={`${process.env.REACT_APP_BACKEND_URL}${active.processed_image_url || active.raw_image_url}`}
                             className="max-h-full max-w-full object-contain"/>
                      ) : (
                        <Loader2 className="w-6 h-6 text-slate-300 animate-spin"/>
                      )}
                    </div>

                    {/* OCR result */}
                    <div className="text-xs">
                      {active.face_flag && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700">
                          <p className="font-bold flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5"/> Face detected</p>
                          <p className="text-[11px] mt-1">For DPDP §10 compliance we don't process board photos that contain people's faces. Delete and retake without anyone in frame.</p>
                        </div>
                      )}
                      {active.ocr_status === 'failed' && !active.face_flag && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800">
                          <p className="font-bold flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5"/> OCR failed</p>
                          <p className="text-[11px] mt-1">{active.error_detail || 'The image could not be processed.'}</p>
                        </div>
                      )}
                      {active.ocr_status === 'done' && active.extracted && (
                        <div className="space-y-3">
                          <div>
                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Extracted text</div>
                            <pre className="font-sans whitespace-pre-wrap bg-slate-50 border border-slate-100 rounded-lg p-2.5 max-h-44 overflow-y-auto text-[12px] text-slate-800" data-testid="bcp-extracted-text">
{active.extracted.text || '(empty)'}
                            </pre>
                          </div>
                          {(active.extracted.latex_blocks || []).length > 0 && (
                            <div>
                              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">LaTeX</div>
                              <ul className="space-y-1">
                                {active.extracted.latex_blocks.map((l, i) => (
                                  <li key={i} className="font-mono text-[12px] bg-emerald-50 border border-emerald-100 rounded px-2 py-1 text-emerald-900">
                                    {l}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {(active.extracted.diagram_json || []).length > 0 && (
                            <div>
                              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Diagrams</div>
                              <ul className="space-y-1">
                                {active.extracted.diagram_json.map((d, i) => (
                                  <li key={i} className="text-[11px] bg-sky-50 border border-sky-100 rounded px-2 py-1 text-sky-900">
                                    <strong>{d.kind || 'diagram'}:</strong> {d.description || '—'}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          <div className="text-[10px] text-slate-400 flex items-center gap-2">
                            <Brain className="w-3 h-3"/> confidence {(active.extracted.confidence * 100).toFixed(0)}%
                            {active.extracted.mathpix_mocked && <span className="px-1 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-500 font-bold">Mathpix MOCK</span>}
                          </div>
                        </div>
                      )}
                      {['pending', 'processing'].includes(active.ocr_status) && (
                        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-blue-800 inline-flex items-center gap-2">
                          <Loader2 className="w-3.5 h-3.5 animate-spin"/> OCR running — refreshing automatically…
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer — generate-with-board CTA */}
        <div className="border-t border-slate-100 px-5 py-4 flex items-center gap-3 flex-wrap" data-testid="bcp-footer">
          <button onClick={fetchCaps} data-testid="bcp-refresh"
                  className="h-10 px-3 text-sm font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 inline-flex items-center gap-1.5">
            <RefreshCw className="w-3.5 h-3.5"/> Refresh
          </button>
          <button onClick={generate} disabled={generating || captures.length === 0}
                  data-testid="bcp-generate-btn"
                  className="ml-auto h-10 px-5 text-sm font-bold text-white rounded-lg shadow inline-flex items-center gap-2 hover:shadow-md disabled:opacity-50"
                  style={{ background: GREEN }}>
            {generating ? <Loader2 className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4"/>}
            Generate lesson with board
          </button>
        </div>
      </div>
    </div>
  );
}
